import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface InfinitePollingOptions {
  interval?: number;
  maxRetries?: number;
  retryDelay?: number;
  enabled?: boolean;
  dependencies?: ReadonlyArray<unknown>;
  immediate?: boolean;
}

export interface InfinitePollingResult<TData> {
  data: TData | null;
  error: unknown;
  isPolling: boolean;
  start: () => void;
  stop: () => void;
  reset: () => void;
}

export function useInfinitePolling<TData>(
  apiFn: () => Promise<TData>,
  options: InfinitePollingOptions = {}
): InfinitePollingResult<TData> {
  const {
    interval = 5000,
    maxRetries = 3,
    retryDelay = interval,
    enabled = true,
    dependencies = [],
    immediate = true,
  } = options;

  const [data, setData] = useState<TData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptsRef = useRef<number>(0);
  const isActiveRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(false);
  const apiRef = useRef(apiFn);

  apiRef.current = apiFn;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    isActiveRef.current = false;
    setIsPolling(false);
    clearTimer();
  }, [clearTimer]);

  const scheduleNext = useCallback(
    (delayMs: number) => {
      clearTimer();
      if (!isActiveRef.current) return;
      timeoutRef.current = setTimeout(async () => {
        if (!isActiveRef.current) return;
        try {
          const result = await apiRef.current();
          if (!isMountedRef.current || !isActiveRef.current) return;
          setData(result);
          setError(null);
          attemptsRef.current = 0;
          scheduleNext(interval);
        } catch (err) {
          if (!isMountedRef.current || !isActiveRef.current) return;
          setError(err);
          attemptsRef.current += 1;
          if (attemptsRef.current > maxRetries) {
            stop();
            return;
          }
          scheduleNext(retryDelay);
        }
      }, delayMs);
    },
    [interval, retryDelay, maxRetries, clearTimer, stop]
  );

  const start = useCallback(() => {
    if (isActiveRef.current) return;
    isActiveRef.current = true;
    setIsPolling(true);
    attemptsRef.current = 0;
    scheduleNext(immediate ? 0 : interval);
  }, [scheduleNext, immediate, interval]);

  const reset = useCallback(() => {
    attemptsRef.current = 0;
    setError(null);
    setData(null);
  }, []);

  // Mount/unmount lifecycle
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stop();
    };
  }, [stop]);

  // React to options/enabled/dependencies changes
  const depsKey = useMemo(() => dependencies, [dependencies]);
  useEffect(() => {
    stop();
    if (enabled) {
      start();
    }
    // Cleanup handled by stop() on dependency change and unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, stop, enabled, interval, retryDelay, maxRetries, immediate, apiRef, ...depsKey]);

  return { data, error, isPolling, start, stop, reset };
}

export default useInfinitePolling;



